import { computed, ref, unref } from 'vue';
import { useLiveQueryV2, CODES } from '@libs/use';
import { SummarizedOrder } from '@libs/api-reference';
import {
  createNewOmsUserChannelName,
  createUserChannelName,
  useUser,
} from '@core/user';
import { useSymbolDeductions } from '@core/symbol';
import { MaybeRef } from '@vueuse/core';

import * as API from '../requests';
import {
  transformNewOmsOrderAndTradeChange,
  transformOrderAndTradeChange,
} from '../utils/transform/orderTrade';
import { ORDER_SIDES, ORDERS_QUERY_KEYS } from '../constants';

export default function useLiveOrderSummarizeQuery(
  { immediate }: { immediate: MaybeRef<boolean> } = { immediate: false }
) {
  const { isUserReady, user } = useUser();
  const { findWageConfigBySymbol } = useSymbolDeductions();

  const hasNewSummarize = ref(false);

  function calculateAveragePrice(
    previousData: SummarizedOrder,
    currentTrade: ReturnType<typeof transformOrderAndTradeChange>
  ) {
    const previousTotal =
      (previousData.quantity ?? 0) * previousData.averagePrice;
    const currentTotal = currentTrade.tradePrice * currentTrade.tradeQuantity;

    return (
      (previousTotal + currentTotal) /
      (previousData.quantity + currentTrade.tradeQuantity)
    );
  }

  function calculateSum(item: SummarizedOrder) {
    if (item.totalValue) return item.totalValue;

    const total = item.quantity ? item.quantity * item.averagePrice : 0;
    const wage = item.wage || 0;

    if (item.orderSideId === ORDER_SIDES.buy) {
      return total + wage;
    } else {
      return total - wage;
    }
  }

  function calculateTotalWage(
    data: SummarizedOrder,
    wageObject: ReturnType<typeof findWageConfigBySymbol>
  ) {
    const wagePercent =
      data.orderSideId === ORDER_SIDES.buy
        ? wageObject.buyValue
        : wageObject.sellValue;

    return data.averagePrice * (data.quantity ?? 0) * wagePercent;
  }

  function addToList(data: ReturnType<typeof transformOrderAndTradeChange>) {
    const item = {
      averagePrice: data.tradePrice,
      instrumentCode: '',
      instrumentId: 0,
      instrumentIdentification: data.instrumentIdentification,
      instrumentTitle: data.instrumentName,
      orderSideId: data.orderSideId,
      quantity: data.tradeQuantity,
      tradeCount: 1,
      tradeDate: new Date().toISOString(),
      wage: 0,
      instrumentTypeCode: data.instrumentTypeCode,
      sectorCode: data.sectorCode,
      subSectorCode: data.subSectorCode,
    };

    result.value?.data.unshift({
      ...item,
      totalValue: calculateSum(item as SummarizedOrder),
      wage: calculateTotalWage(
        item as SummarizedOrder,
        findWageConfigBySymbol({
          instrumentTypeCode: data.instrumentTypeCode,
          subSectorCode: data.subSectorCode,
        })
      ),
    });
  }

  const {
    result,
    execute: getOrderSummarize,
    isIdle: isWaitingForOrderSummarize,
    isPending: isLoadingOrderSummarize,
    isError: isErrorLoadingOrderSummarize,
    isSuccess: isSuccessLoadingOrderSummarize,
    status: orderSummarizeStatus,
  } = useLiveQueryV2({
    immediate: computed(() => unref(isUserReady) && unref(immediate)),
    staleTime: 3000,
    fetcher: API.getOrderSummarize,
    key: ORDERS_QUERY_KEYS.orderSummarize,
    getChannels: () => {
      return [
        createUserChannelName(user.value?.userName),
        createNewOmsUserChannelName(user.value?.userId),
      ];
    },
    unsubscribe: false,
    onUpdate(signal) {
      const actions = {
        [CODES.OmsOrderAndTradeSignal]: transformOrderAndTradeChange,
        [CODES.NewOmsOrderAndTradeSignal]: transformNewOmsOrderAndTradeChange,
      };

      if (actions[signal.code]) {
        const data = actions[signal.code](signal.data);

        if (signal.code === CODES.OmsOrderAndTradeSignal) {
          if (
            data.messageType === 'executeTrade' &&
            data.orderStateEnglishTitle === 'Executed' &&
            data.investorBourseCodeId === user.value?.investorBourseCodes[0].id
          ) {
            hasNewSummarize.value = true;

            if (result.value?.data.length) {
              const previousDataIndex = result.value.data.findIndex(
                (d) =>
                  d.instrumentIdentification ===
                    data.instrumentIdentification &&
                  d.orderSideId === data.orderSideId
              );

              if (previousDataIndex > -1) {
                const previousData = result.value.data[previousDataIndex];

                const wage = findWageConfigBySymbol({
                  instrumentTypeCode: data.instrumentTypeCode,
                  subSectorCode: data.subSectorCode,
                });

                const newData = {
                  ...previousData,
                  instrumentTitle: data.instrumentName,
                  quantity: previousData.quantity + data.tradeQuantity,
                  tradeCount: previousData.tradeCount + 1,
                  averagePrice: calculateAveragePrice(previousData, data),
                };

                result.value.data[previousDataIndex] = {
                  ...newData,
                  wage: wage ? calculateTotalWage(newData, wage) : newData.wage,
                };
              } else {
                addToList(data);
              }
            } else {
              addToList(data);
            }
          }
        } else if (signal.code === CODES.NewOmsOrderAndTradeSignal) {
          if (
            data.messageType === 'executeTrade' &&
            data.orderStateEnglishTitle === 'Executed'
          ) {
            hasNewSummarize.value = true;

            if (result.value?.data.length) {
              const previousDataIndex = result.value.data.findIndex(
                (d) =>
                  d.instrumentIdentification ===
                    data.instrumentIdentification &&
                  d.orderSideId === data.orderSideId
              );

              if (previousDataIndex > -1) {
                const previousData = result.value.data[previousDataIndex];

                const wage = findWageConfigBySymbol({
                  instrumentTypeCode: data.instrumentTypeCode,
                  subSectorCode: data.subSectorCode,
                });

                const newData = {
                  ...previousData,
                  instrumentTitle: data.instrumentName,
                  quantity: previousData.quantity + data.tradeQuantity,
                  tradeCount: previousData.tradeCount + 1,
                  averagePrice: calculateAveragePrice(previousData, data),
                };

                result.value.data[previousDataIndex] = {
                  ...newData,
                  wage: wage ? calculateTotalWage(newData, wage) : newData.wage,
                };
              } else {
                addToList(data);
              }
            } else {
              addToList(data);
            }
          }
        }
      }
    },
  });

  const summarizedOrders = computed(
    () =>
      result.value?.data.map((item) => ({
        ...item,
        calculatedSum: calculateSum(item),
      })) || []
  );

  return {
    summarizedOrders,
    isWaitingForOrderSummarize,
    isErrorLoadingOrderSummarize,
    isLoadingOrderSummarize,
    isSuccessLoadingOrderSummarize,
    getOrderSummarize,
    hasNewSummarize,
    orderSummarizeStatus,
  };
}
